import type { LoaderFunctionArgs } from '~/types/route-args';
import type { ShouldRevalidateFunction } from 'react-router';
import { data as routerData } from 'react-router';
import {
    buildTrendingSearchResults,
    querySearchServiceSearch,
    TRENDING_RESULTS_SIZE,
} from '@modules/search';
import type { SearchIndex } from '@modules/search/type/api';
import { DEFAULT_SOURCE_FILTER } from '@modules/search/util/filter-url-helpers';

export const FEATURED_RESULTS_ACTION = '/actions/search/featured-results';

/**
 * API endpoint to get curated trending results for the mobile search modal.
 */
export const loader = async ({ context }: LoaderFunctionArgs) => {
    const { logger } = context;

    try {
        const [
            projectResults,
            productResults,
            professionalResults,
            articleResults,
            overallTrendingResults,
        ] = await Promise.all([
            querySearchServiceSearch({
                context,
                queryParams: getTrendingQueryParams({
                    searchType: 'project',
                    size: 1,
                }),
            }),
            querySearchServiceSearch({
                context,
                queryParams: getTrendingQueryParams({
                    searchType: 'product',
                    size: 1,
                }),
            }),
            querySearchServiceSearch({
                context,
                queryParams: getTrendingQueryParams({
                    searchType: 'professional',
                    size: 1,
                }),
            }),
            querySearchServiceSearch({
                context,
                queryParams: getTrendingQueryParams({
                    searchType: 'blogpost',
                    size: 1,
                }),
            }),
            querySearchServiceSearch({
                context,
                queryParams: getTrendingQueryParams({
                    size: TRENDING_RESULTS_SIZE,
                }),
            }),
        ]);

        const results = buildTrendingSearchResults({
            primaryHits: [
                projectResults?.hits[0],
                productResults?.hits[0],
                professionalResults?.hits[0],
                articleResults?.hits[0],
            ],
            fallbackHits: overallTrendingResults?.hits ?? [],
        });

        return { results };
    } catch (error) {
        logger.error('Error in /actions/search/featured-results', { error });
        return routerData(null, { status: 500 });
    }
};

export const shouldRevalidate: ShouldRevalidateFunction = () => {
    return false;
};

function getTrendingQueryParams({
    searchType,
    size,
}: {
    searchType?: SearchIndex;
    size: number;
}) {
    return {
        visibilityType: 'directory' as const,
        sourceFilter: DEFAULT_SOURCE_FILTER,
        sortBy: 'trending' as const,
        size,
        ...(searchType ? { searchType } : {}),
    };
}
